import React, { useEffect, useState } from "react";
import { BarChart3, Image as ImageIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { ResponsiveContainer } from "recharts";
import { Button } from "../ui/button";

const S3_BUCKET = "saudican-processed";

const AnomalyCard = () => {
  const [imagePaths, setImagePaths] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [defectName, setDefectName] = useState("");
  const [editing, setEditing] = useState(false);

  const fetchImageKeys = async () => {
    try {
      const productName = "product1";
      const size = "small";

      const response = await fetch(
        `https://eyonyjgai6.execute-api.us-east-1.amazonaws.com/${productName}/${size}/anomalies`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        setImagePaths(data.imageKeys);
      } else {
        console.error("Error fetching images:", data.error);
      }
    } catch (error) {
      console.error("Error fetching images:", error);
    }
  };

  useEffect(() => {
    fetchImageKeys();
  }, []);

  const openModal = (imagePath) => {
    const parts = imagePath.split("_");
    const rawDefectName = parts
      .slice(7)
      .join("_")
      .replace(".jpg", "")
      .replace(/_/g, " ");

    setDefectName(rawDefectName);
    setSelectedImage(imagePath);
  };

  const closeModal = () => {
    setSelectedImage(null);
    setEditing(false);
  };

  const classifyDefect = async () => {
    if (!defectName || !selectedImage) {
      alert("Please ensure both defect name and selected image are provided.");
      return;
    }

    const apiUrl =
      "https://eyonyjgai6.execute-api.us-east-1.amazonaws.com/prod/classify";

    const requestData = {
      selectedImage: selectedImage,
      defectName: defectName,
      product: "product1",
      size: "small",
    };

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(
          `Server error: ${response.status} ${response.statusText}`
        );
      }

      setImagePaths((prevPaths) =>
        prevPaths.filter((path) => path !== selectedImage)
      );
      setDefectName("");
      setSelectedImage(null);
      setEditing(false);
      closeModal();

      alert(
        `The anomaly defect has been successfully classified as '${defectName}'`
      );
    } catch (error) {
      alert(`Error: ${error.message}`);
      console.error("Error classifying defect:", error);
    }
  };
  const updateDefectType = async () => {
    if (!defectName || !selectedImage) {
      alert("Please provide both defect name and selected image.");
      return;
    }

    const newDefectName = defectName.replace(/\s+/g, "_");

    try {
      const response = await fetch(
        "https://eyonyjgai6.execute-api.us-east-1.amazonaws.com/prod/update-defect",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            selectedImage,
            newDefectName,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update defect type.");
      }

      const data = await response.json();
      alert(data.message);

      // Update UI
      const parts = selectedImage.split("_");
      const newImagePath = `${parts.slice(0, 7).join("_")}_${newDefectName}.jpg`;

      setImagePaths((prevPaths) =>
        prevPaths.filter((path) => path !== selectedImage).concat(newImagePath)
      );
      setSelectedImage(newImagePath);
      setEditing(false);
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
  };
  // Handle deletion of image
  const handleNoDefect = async () => {
    if (!selectedImage) {
      alert("Please provide both defect name and selected image.");
      return;
    }
    try {
      const response = await fetch(
        "https://eyonyjgai6.execute-api.us-east-1.amazonaws.com/prod/delete-image",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ imageKey: selectedImage }),
        }
      );

      const data = await response.json();
      if (response.ok) {
        setImagePaths((prevPaths) =>
          prevPaths.filter((path) => path !== selectedImage)
        );
        closeModal();
        alert("Image deleted successfully!");
      } else {
        throw new Error(data.message || "Failed to delete the image.");
      }
    } catch (error) {
      console.error("Error deleting image:", error);
      alert("Failed to delete the image.");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-blue-400" />
          <h2 className="text-sm font-semibold text-slate-200">
            Anomaly Defect List
          </h2>
        </div>
      </div>

      <div className="h-[355px] sm:h-[355px] 2xl:h-[355px]">
        <ResponsiveContainer width="100%" height="100%">
          <div
            className="relative overflow-x-auto overflow-y-auto scroll-smooth shadow-md h-full"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "#1f2937 transparent",
            }}
          >
            {imagePaths.length === 0 ? (
              <div className="flex justify-center items-center text-slate-200 py-11">
                <span>No anomaly defects found.</span>
              </div>
            ) : (
              <table className="w-full text-sm text-left text-gray-400">
                <thead className="text-xs uppercase border-b border-t border-slate-800/60 text-gray-400">
                  <tr>
                    <th className="px-4 py-3">Defect image</th>
                    <th className="px- py-3 text-center hidden sm:table-cell  ">
                      Defect Name
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {imagePaths.map((imagePath) => {
                    const parts = imagePath.split("_");
                    const defectName = parts
                      .slice(7)
                      .join("_")
                      .replace(".jpg", "")
                      .replace(/_/g, " ");

                    return (
                      <tr
                        key={imagePath}
                        className="border-b border-slate-800/60 hover:bg-slate-900/80"
                      >
                        <th
                          scope="row"
                          className="px-6 py-2 font-medium text-white sm:text-center" // Use sm:text-center for centering on small screens
                        >
                          <div
                            className="cursor-pointer bg-slate-800 p-2 rounded-full flex items-center justify-center sm:w-14 sm:h-14 w-20 h-20 mx-auto" // Added mx-auto for centering
                            onClick={() => openModal(imagePath)}
                          >
                            <img
                              src={`https://${S3_BUCKET}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${imagePath}`}
                              alt="Defect"
                              className="w-14 h-14 object-cover rounded-full sm:w-10 sm:h-10 "
                            />
                          </div>
                        </th>

                        <td className="px-4 py-2 hidden sm:table-cell text-center">
                          {defectName}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </ResponsiveContainer>
      </div>

      {selectedImage && (
        <Dialog open={!!selectedImage} onOpenChange={closeModal}>
          <DialogContent className="max-w-xl bg-slate-900/100 backdrop-blur-sm border border-slate-800/60 text-slate-100 rounded-lg p-4 sm:h-auto h-full">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg font-bold">
                <ImageIcon className="h-6 w-6 text-blue-400" />
                Defect Image
              </DialogTitle>
            </DialogHeader>

            <div className="mt-4">
              <img
                src={`https://${S3_BUCKET}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${selectedImage}`}
                alt="Defect Preview"
                className="w-full h-80 sm:h-56 object-contain rounded"
              />
              <div className="mt-2 text-center">
                {editing ? (
                  <input
                    type="text"
                    value={defectName}
                    onChange={(e) => {
                      const capitalizeWords = (text) => {
                        const exceptions = ["or", "and"]; // Add more words if needed
                        return text
                          .split(" ")
                          .map((word, index) => {
                            const lowerCaseWord = word.toLowerCase();
                            if (
                              exceptions.includes(lowerCaseWord) &&
                              index !== 0
                            ) {
                              return lowerCaseWord; // Keep the exception words lowercase
                            }
                            return (
                              word.charAt(0).toUpperCase() +
                              word.slice(1).toLowerCase()
                            );
                          })
                          .join(" ");
                      };

                      // Remove unwanted characters (e.g., commas, dots)
                      const sanitizedInput = e.target.value.replace(
                        /[^a-zA-Z0-9 ]/g,
                        ""
                      );
                      const formattedName = capitalizeWords(sanitizedInput);
                      setDefectName(formattedName);
                    }}
                    className="bg-slate-950/60 rounded-lg p-2 backdrop-blur-sm border border-slate-800/60 text-white text-center w-1/2"
                    placeholder="Enter defect name"
                  />
                ) : (
                  <p className="text-slate-200 p-2 text-center">{defectName}</p>
                )}
              </div>
            </div>

            <div className="flex justify-between items-center mt-6 flex-wrap gap-4">
              <Button
                variant="outline"
                className="hover:bg-green-600/20 sm:w-1/3 w-1/4"
                onClick={handleNoDefect}
              >
                No Defect
              </Button>

              <div className="flex justify-end gap-2   w-1/2">
                <Button
                  variant="outline"
                  className="hover:bg-blue-600/20 w-1/2"
                  onClick={() => {
                    if (editing) {
                      updateDefectType(); // Call your update function when confirming
                    } else {
                      setEditing(true); // Enable editing mode
                    }
                  }}
                >
                  {editing ? "Confirm" : "Edit Type"}
                </Button>

                <Button
                  variant="outline"
                  className="hover:bg-yellow-600/20 w-1/2"
                  onClick={classifyDefect}
                >
                  Classify
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AnomalyCard;
